/**
 * AuthContext wires the module-level `authStore` into the React tree so
 * components re-render when sign-in / sign-out happens. The store is still the
 * source of truth — this context just publishes ticks and exposes the derived
 * operator identity (email / name / tenant / roles) decoded from the JWT claims.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  getAccessToken,
  getClaims,
  registerSignOutCallback,
  setAccessToken,
  signOut as storeSignOut,
} from './authStore';
import { rolesFromClaims, tenantSlugFromClaims, type JwtPayload } from '@/utils/jwt';

export type AuthStatus = 'booting' | 'authed' | 'unauthed';

/**
 * Whether a role set grants the admin surface (wsr-api gates OWNER / SUPER_ADMIN / ADMIN).
 * SUPER_ADMIN is included so that support tokens (tenant_role: SUPER_ADMIN) and any future
 * tenant SUPER_ADMIN are recognised correctly.
 */
const ADMIN_ROLES = new Set(['OWNER', 'SUPER_ADMIN', 'ADMIN']);

/** Exact claim value that identifies a platform-support JWT (SASATH-0313). */
const PLATFORM_SUPPORT_PRINCIPAL_TYPE = 'PLATFORM_SUPPORT';

export interface CurrentOperator {
  email: string;
  name: string | undefined;
  tenantSlug: string | undefined;
  roles: string[];
  /** True when the operator holds OWNER, SUPER_ADMIN, or ADMIN (the wsr-api admin gate). */
  isAdmin: boolean;
  /** True only when principal_type === 'PLATFORM_SUPPORT' (exact value). */
  isPlatformSupport: boolean;
}

interface AuthContextValue {
  status: AuthStatus;
  operator: CurrentOperator | null;
  setAuthed: (token: string, expiresAt: string | null) => void;
  setUnauthed: () => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function deriveOperator(claims: JwtPayload | null): CurrentOperator | null {
  if (!claims) return null;
  const roles = rolesFromClaims(claims);
  const isPlatformSupport = claims.principal_type === PLATFORM_SUPPORT_PRINCIPAL_TYPE;
  return {
    email: claims.email ?? claims.sub ?? '',
    name: claims.name,
    tenantSlug: tenantSlugFromClaims(claims),
    roles,
    isAdmin: roles.some((r) => ADMIN_ROLES.has(r)),
    isPlatformSupport,
  };
}

export interface AuthProviderProps {
  children: ReactNode;
  initialStatus?: AuthStatus;
}

export function AuthProvider({ children, initialStatus = 'booting' }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>(initialStatus);
  const [operator, setOperator] = useState<CurrentOperator | null>(() =>
    deriveOperator(getClaims()),
  );

  const refreshFromStore = useCallback(() => {
    const token = getAccessToken();
    setStatus(token ? 'authed' : 'unauthed');
    setOperator(deriveOperator(getClaims()));
  }, []);

  useEffect(() => {
    registerSignOutCallback(() => {
      setStatus('unauthed');
      setOperator(null);
    });
    return () => registerSignOutCallback(null);
  }, []);

  const setAuthed = useCallback(
    (token: string, expiresAt: string | null) => {
      setAccessToken(token, expiresAt);
      refreshFromStore();
    },
    [refreshFromStore],
  );

  const setUnauthed = useCallback(() => {
    setAccessToken(null);
    setStatus('unauthed');
    setOperator(null);
  }, []);

  const handleSignOut = useCallback(() => {
    storeSignOut();
    setStatus('unauthed');
    setOperator(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, operator, setAuthed, setUnauthed, signOut: handleSignOut }),
    [status, operator, setAuthed, setUnauthed, handleSignOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

export function useCurrentOperator(): CurrentOperator | null {
  return useAuth().operator;
}

export function useAuthStatus(): AuthStatus {
  return useAuth().status;
}
